import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MaskedLine, FadeUp } from "@/components/motion";
import { Sparkle, ArrowDiag } from "@/components/Icons";
import { Float, Blob } from "@/components/Mascots";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_TYPES = ["Site web", "Application", "Web app", "Autre"];

const inputClass =
  "w-full bg-transparent border-b-2 border-white/20 focus:border-flytab-green outline-none rounded-none py-4 font-mono text-lg md:text-2xl text-flytab-cream placeholder:text-neutral-600 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", project_type: "Site web", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    const succeed = () => {
      setSent(true);
      toast.success("Brief reçu 5/5. On revient vers toi très vite.");
    };
    try {
      // Netlify Forms (prod sur Netlify) — repli sur l'API backend en preview/autre hébergement
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ "form-name": "contact", ...form }).toString(),
      });
      if (!res.ok) throw new Error("netlify forms unavailable");
      succeed();
    } catch {
      try {
        await axios.post(`${API}/contact`, form);
        succeed();
      } catch (err) {
        toast.error("Oups — l'envoi a échoué. Réessaie dans un instant.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-flytab-black text-flytab-cream px-4 md:px-10 py-24 md:py-36 border-t border-flytab-line" data-testid="contact-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5 flex flex-col justify-between">
          <div>
            <FadeUp>
              <span className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-flytab-green">(Contact)</span>
            </FadeUp>
            <h2 className="font-display font-black uppercase leading-[0.9] tracking-tight text-5xl md:text-7xl mt-6" data-testid="contact-heading">
              <MaskedLine delay={0.1}>On crée</MaskedLine>
              <MaskedLine delay={0.2}>quoi</MaskedLine>
              <MaskedLine delay={0.3} innerClassName="text-flytab-green">ensemble ?</MaskedLine>
            </h2>

            <Float className="mt-10 hidden md:block" amp={14} dur={5.5}>
              <Blob className="w-32 lg:w-40" />
            </Float>
          </div>

          <FadeUp delay={0.35} className="mt-12 lg:mt-0">
            <div className="font-mono text-sm md:text-base space-y-3 text-neutral-400">
              <p>
                <a href="mailto:contact@flytab.fr" className="text-flytab-cream underline decoration-flytab-green decoration-2 underline-offset-4 hover:text-flytab-green transition-colors" data-testid="contact-email-link">
                  contact@flytab.fr
                </a>
              </p>
              <p>Paris — Worldwide</p>
              <p className="flex items-center gap-2">
                <Sparkle className="w-4 h-4" stroke="none" strokeWidth={0} />
                Réponse sous 24h, promis.
              </p>
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          {sent ? (
            <div className="h-full min-h-[420px] border-2 border-flytab-green flex flex-col items-center justify-center text-center p-10" data-testid="contact-success-message">
              <Sparkle className="w-14 h-14 mb-8" stroke="none" strokeWidth={0} />
              <p className="font-display font-black uppercase text-3xl md:text-5xl leading-tight mb-4">
                Message<br />envoyé.
              </p>
              <p className="font-mono text-sm md:text-base text-neutral-400 mb-8">
                Ton brief est bien arrivé. On te répond sous 24h.
              </p>
              <button
                onClick={() => { setSent(false); setForm({ name: "", email: "", project_type: "Site web", message: "" }); }}
                className="rounded-full border-2 border-flytab-green text-flytab-green font-mono font-bold uppercase text-xs tracking-wider px-6 py-3 hover:bg-flytab-green hover:text-flytab-black transition-colors"
                data-testid="contact-send-another-button"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <FadeUp delay={0.2}>
              <form onSubmit={submit} name="contact" className="space-y-10" data-testid="contact-form">
                <div>
                  <label htmlFor="contact-name" className="block font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 mb-1">
                    Ton nom *
                  </label>
                  <input
                    id="contact-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jeanne Dupont"
                    className={inputClass}
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 mb-1">
                    Ton email *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jeanne@studio.com"
                    className={inputClass}
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <span className="block font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 mb-4">
                    Type de projet *
                  </span>
                  <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Type de projet">
                    {PROJECT_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, project_type: type }))}
                        className={`rounded-full px-6 py-3 font-mono text-sm uppercase tracking-wider border-2 transition-colors duration-200 ${
                          form.project_type === type
                            ? "bg-flytab-green text-flytab-black border-flytab-green font-bold"
                            : "border-white/25 text-neutral-300 hover:border-flytab-green hover:text-flytab-green"
                        }`}
                        data-testid={`contact-type-${type.toLowerCase().replace(/\s/g, "-")}`}
                        aria-pressed={form.project_type === type}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className="block font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 mb-1">
                    Parle-nous du projet *
                  </label>
                  <textarea
                    id="contact-message"
                    required
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="On veut un site qui décoiffe…"
                    className={`${inputClass} resize-none`}
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-flytab-green text-flytab-black font-display font-bold uppercase text-base md:text-lg px-10 py-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_0_#1FA75A] disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-none inline-flex items-center gap-2"
                  data-testid="contact-submit-button"
                >
                  {loading ? "Envoi en cours…" : "Envoyer le brief"}
                  {!loading && <ArrowDiag className="w-5 h-5" />}
                </button>
                <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.2em] text-neutral-500" data-testid="contact-price-hint">
                  Devis gratuit ✦ Réponse sous 24h ✦ Sans engagement
                </p>
              </form>
            </FadeUp>
          )}
        </div>
      </div>
    </section>
  );
}
